/**
 * Passport Field Visibility Policy — Round 73 (A-C-02)
 *
 * Three-class field classification governing what is exposed in each
 * Passport view mode. Used as a defense-in-depth layer alongside the
 * server-side RPC nullification.
 *
 * Classes
 * -------
 *  - user_private:  Owner-only, never exposed to anyone else.
 *  - operational:   Visible to in-scope managers AND the owner.
 *                   Hierarchy users need this to make staffing/dev decisions.
 *  - connect_only:  Visible only in Connect discovery context (recruiting flow).
 *                   Hidden in operational in-scope view.
 *
 * Phase 1 implements the exact split below; future rounds may refine.
 */

export type PassportViewMode = 'self' | 'connect' | 'in_scope';
export type PassportFieldClass = 'user_private' | 'operational' | 'connect_only';

/** Authoritative classification table. */
export const FIELD_CLASSES: Record<string, PassportFieldClass> = {
  // Operational — always visible to in-scope viewers and owner
  user_id: 'operational',
  display_name: 'operational',
  roles: 'operational',
  technician_profile_id: 'operational',
  technician_type_title: 'operational',
  technician_type_source: 'operational',

  // Connect-only — recruiting context fields, hidden from in-scope view
  open_to_work_status: 'connect_only',
  visible_to_shops: 'connect_only',
  distance_miles: 'connect_only',
  share_notes_with_shops: 'connect_only',
  references_recruiting: 'connect_only',

  // User-private — never exposed
  home_zip: 'user_private',
  radius_miles: 'user_private',
  personal_contact: 'user_private',

  // Core-100 R2 (CORE-K GAP-002): the Toolbox's financially sensitive fields are user-private; what a tool
  // is (name, category, brand, model) is operational. The database grants the Toolbox to no audience but
  // its owner today, so this is the deny-by-default classification any future projection must respect.
  tool_purchase_price: 'user_private',
  tool_purchase_date: 'user_private',
  tool_serial_number: 'user_private',
  tool_notes: 'user_private',
  tool_source_document: 'user_private',
  tool_name: 'operational',
  tool_category: 'operational',
  tool_brand: 'operational',
  tool_model: 'operational',

  // Core-100 R4 (CORE-F / CORE-E): the portable proficiency record. Operational, matching the server grant
  // (the person, current-employer managers, and opted-in recruiting audiences read technician_proficiencies);
  // the class and its basis travel WITH the value so no audience sees a figure without its provenance.
  proficiency_score: 'operational',
  confidence_score: 'operational',
  sample_size: 'operational',
  provenance_class: 'operational',
};

/**
 * Returns true if a field is visible in the given view mode.
 *
 *  - self:      everything — a person sees every fact about themselves
 *  - unknown:   withheld outside self mode; see the note in the body
 *  - connect:   operational + connect_only
 *  - in_scope:  operational only
 */
export function isFieldVisible(fieldName: string, mode: PassportViewMode): boolean {
  // The owner sees every fact about themselves, `user_private` included.
  if (mode === 'self') return true;

  const cls = FIELD_CLASSES[fieldName];

  // An unclassified field is WITHHELD, not shown.
  //
  // This used to read `return mode !== 'in_scope' ? true : true` — both arms
  // true, so every unknown field was visible in every mode. That is the
  // failure CORE-Q-SEC-001 and CORE-Q-SEC-002 describe from two directions:
  // the table is incomplete, and nothing obliges a new field to be added to
  // it. Together they meant a Passport fact became visible to a shop by the
  // sole virtue of somebody having exposed it, which is the opposite of how
  // a person-controlled record must behave.
  //
  // Product ruling, 2026-09-06: "Unknown/unclassified Passport facts must
  // fail closed, not become visible merely because code exposed them."
  //
  // Fail-closed is only half of an answer, because a field that is silently
  // withheld is as wrong as one that is silently shown — it just fails in
  // the safer direction. The other half is the contract test in the CORE-Q
  // runtime proof, which takes the keys of a real row from each Passport RPC
  // and fails if any of them is unclassified. A new field therefore breaks
  // the proof rather than quietly disappearing from a view.
  if (!cls) return false;

  switch (mode) {
    case 'connect':
      return cls === 'operational' || cls === 'connect_only';
    case 'in_scope':
      return cls === 'operational';
    default:
      return false;
  }
}

/**
 * Filter a Passport-shaped object by view mode. Connect-only and user-private
 * fields are stripped (set to null) when not allowed in `mode`.
 */
export function filterPassportFields<T extends Record<string, any>>(
  profile: T,
  mode: PassportViewMode,
): T {
  if (!profile) return profile;
  const out: Record<string, any> = { ...profile };
  for (const key of Object.keys(out)) {
    if (!isFieldVisible(key, mode)) {
      out[key] = null;
    }
  }
  return out as T;
}
